import { Component, Input, Output, EventEmitter, ElementRef, forwardRef, OnDestroy, OnInit, NgZone } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DropdownOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-custom-dropdown',
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDropdownComponent),
      multi: true
    }
  ]
})
export class CustomDropdownComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private static openInstance: CustomDropdownComponent | null = null;

  @Input() options: DropdownOption[] = [];
  @Input() placeholder: string = 'Select an option';
  @Input() disabled: boolean = false;
  @Input() height?: string;
  @Input() width?: string;
  @Input() borderColor?: string;
  @Input() textColor?: string;
  @Output() selectionChange = new EventEmitter<string>();

  isOpen: boolean = false;
  selectedValue: string = '';
  
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private clickOutsideHandler: (event: MouseEvent) => void;

  constructor(private elementRef: ElementRef, private ngZone: NgZone) {
    this.clickOutsideHandler = (event: MouseEvent) => {
      if (!this.elementRef.nativeElement.contains(event.target as Node)) {
        if (this.isOpen) {
          this.ngZone.run(() => {
            this.isOpen = false;
            if (CustomDropdownComponent.openInstance === this) {
              CustomDropdownComponent.openInstance = null;
            }
          });
        }
      }
    };
  }

  ngOnInit(): void {
    document.addEventListener('click', this.clickOutsideHandler, false);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.clickOutsideHandler, false);
    if (CustomDropdownComponent.openInstance === this) {
      CustomDropdownComponent.openInstance = null;
    }
  }

  get selectedLabel(): string {
    const selected = this.options.find(opt => opt.value === this.selectedValue);
    return selected ? selected.label : this.placeholder;
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      if (CustomDropdownComponent.openInstance && CustomDropdownComponent.openInstance !== this) {
        CustomDropdownComponent.openInstance.isOpen = false;
      }

      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        CustomDropdownComponent.openInstance = this;
        this.onTouched();
      } else {
        CustomDropdownComponent.openInstance = null;
      }
    }
  }

  selectOption(option: DropdownOption): void {
    if (!this.disabled) {
      this.selectedValue = option.value;
      this.isOpen = false;
      CustomDropdownComponent.openInstance = null;
      this.onChange(this.selectedValue);
      this.selectionChange.emit(this.selectedValue);
    }
  }

  isSelected(option: DropdownOption): boolean {
    return this.selectedValue === option.value;
  }



  writeValue(value: string): void {
    this.selectedValue = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

